import { HeartHandshake, Users, Activity, Moon, Baby, CheckCircle2, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/context/LanguageContext";

const ServiceCard = ({ service }) => {
  const isPopular = service.popular;
  return (
    <Card
      data-testid={`service-card-${service.title}`}
      className={`relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-2 ${
        isPopular ? "bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] text-white" : "bg-white"
      }`}
    >
      {isPopular && (
        <div className="absolute top-4 right-4 bg-[#C89D4B] text-white text-xs font-semibold px-3 py-1 rounded-full">
          {service.popularText}
        </div>
      )}
      <CardHeader className="pb-2">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${isPopular ? "bg-white/20" : "bg-[#003A4B]/10"}`}>
          <service.icon className={`w-7 h-7 ${isPopular ? "text-white" : "text-[#003A4B]"}`} />
        </div>
        <CardTitle className={`text-xl ${isPopular ? "text-white" : "text-[#1A2332]"}`}>{service.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-sm mb-3 ${isPopular ? "text-white/80" : "text-[#718096]"}`}>{service.description}</p>
        <div className={`flex items-center gap-2 text-sm mb-3 ${isPopular ? "text-white/80" : "text-[#718096]"}`} data-testid={`service-duration-${service.title}`}>
          <Clock className="w-4 h-4 text-[#C89D4B]" />
          <span>{service.duration}</span>
        </div>
        <p className={`text-2xl font-bold mb-4 ${isPopular ? "text-[#C89D4B]" : "text-[#003A4B]"}`}>{service.price}</p>
        <ul className="space-y-2">
          {service.features.map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#C89D4B]" />
              <span className={`text-sm ${isPopular ? "text-white/90" : "text-[#4A5568]"}`}>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

const PatientTypeCard = ({ type }) => (
  <div
    data-testid={`patient-type-${type.label}`}
    className="text-center p-6 rounded-2xl bg-[#F3F0E7] hover:bg-[#003A4B]/5 transition-colors group cursor-pointer"
  >
    <div className="w-16 h-16 bg-gradient-to-br from-[#003A4B] to-[#1B5E6B] rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
      <type.icon className="w-8 h-8 text-white" />
    </div>
    <h4 className="font-semibold text-[#1A2332] mb-1">{type.label}</h4>
    <p className="text-sm text-[#718096]">{type.desc}</p>
  </div>
);

export const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    { icon: HeartHandshake, title: t.services.basic.title, description: t.services.basic.description, duration: t.services.basic.duration, price: "$20-28/hr", features: [t.services.basic.f1, t.services.basic.f2, t.services.basic.f3], popular: false },
    { icon: Users, title: t.services.personal.title, description: t.services.personal.description, duration: t.services.personal.duration, price: "$24-35/hr", features: [t.services.personal.f1, t.services.personal.f2, t.services.personal.f3], popular: false },
    { icon: Activity, title: t.services.specialized.title, description: t.services.specialized.description, duration: t.services.specialized.duration, price: "$30-50/hr", features: [t.services.specialized.f1, t.services.specialized.f2, t.services.specialized.f3], popular: true, popularText: t.services.specialized.popular },
    { icon: Moon, title: t.services.night.title, description: t.services.night.description, duration: t.services.night.duration, price: "+20%", features: [t.services.night.f1, t.services.night.f2, t.services.night.f3], popular: false },
  ];

  const patientTypes = [
    { icon: Users, label: t.services.elderly, desc: t.services.elderlyDesc },
    { icon: Baby, label: t.services.children, desc: t.services.childrenDesc },
    { icon: Activity, label: t.services.ventilator, desc: t.services.ventilatorDesc },
    { icon: HeartHandshake, label: t.services.palliative, desc: t.services.palliativeDesc },
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 bg-gradient-to-b from-[#F3F0E7] to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-[#C89D4B] font-semibold uppercase tracking-widest text-sm">{t.services.title}</span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A2332] mt-3">{t.services.subtitle}</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {services.map((service) => <ServiceCard key={service.title} service={service} />)}
        </div>

        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-[#003A4B]/10">
          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A2332] text-center mb-10">{t.services.patientTypes}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {patientTypes.map((type) => <PatientTypeCard key={type.label} type={type} />)}
          </div>
        </div>
      </div>
    </section>
  );
};
